import type { CreativeOverrides, OptimizationGoal } from "../types"

const CTA_BY_OBJECTIVE: Record<string, string[]> = {
  "Max Conversions": [
    "SHOP_NOW",
    "SIGN_UP",
    "DOWNLOAD",
    "BOOK_NOW",
    "APPLY_NOW",
    "GET_QUOTE",
    "SUBSCRIBE",
  ],
  "Max Conversion Value": [
    "SHOP_NOW",
    "SIGN_UP",
    "DOWNLOAD",
    "BOOK_NOW",
    "APPLY_NOW",
    "GET_QUOTE",
    "SUBSCRIBE",
  ],
  "Landing Page Views": [
    "LEARN_MORE",
    "SHOP_NOW",
    "SIGN_UP",
    "BOOK_NOW",
    "CONTACT_US",
    "DOWNLOAD",
    "GET_OFFER",
    "GET_QUOTE",
    "WATCH_MORE",
  ],
  "Web Visits": [
    "LEARN_MORE",
    "SHOP_NOW",
    "SIGN_UP",
    "BOOK_NOW",
    "CONTACT_US",
    "DOWNLOAD",
    "GET_OFFER",
    "GET_QUOTE",
    "WATCH_MORE",
  ],
  "Post Engagements": [
    "LEARN_MORE",
    "SHOP_NOW",
    "SIGN_UP",
    "BOOK_NOW",
    "CONTACT_US",
    "DOWNLOAD",
    "GET_OFFER",
    "GET_QUOTE",
    "WATCH_MORE",
  ],
  "Facebook Page Likes": ["LEARN_MORE"],
  "Demand Generation - Awareness": [
    "LEARN_MORE",
    "WATCH_MORE",
    "LISTEN_NOW",
    "SEE_MORE",
  ],
}

function formatCTA(cta: string): string {
  return cta
    .split("_")
    .map(w => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ")
}

interface CreativeDetailsStepProps {
  overrides: CreativeOverrides
  onChange: (overrides: CreativeOverrides) => void
  optimizationGoal: OptimizationGoal
  onNext: () => void
  onBack: () => void
}

export default function CreativeDetailsStep({
  overrides,
  onChange,
  optimizationGoal,
  onNext,
  onBack,
}: CreativeDetailsStepProps) {
  const ctaOptions = CTA_BY_OBJECTIVE[optimizationGoal] ?? []

  const needsUrl =
    optimizationGoal !== "Profile Visits" &&
    optimizationGoal !== "Post Engagements"

  const isValid = !needsUrl || Boolean(overrides.destinationUrl?.trim())

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-basic-white mb-1">
          Creative Details
        </h2>
        <p className="text-sm text-basic-light/60">
          {needsUrl
            ? "Your selected organic posts need a destination URL and optional overrides to run as ads."
            : "Optionally customize how your organic posts appear as ads."}
        </p>
      </div>

      <div className="space-y-4">
        {needsUrl && (
          <div>
            <label className="block text-sm font-medium text-basic-light/70 mb-1.5">
              Destination URL <span className="text-error">*</span>
            </label>
            <input
              type="url"
              placeholder="https://example.com/landing-page"
              value={overrides.destinationUrl ?? ""}
              onChange={e =>
                onChange({ ...overrides, destinationUrl: e.target.value })
              }
              className="input input-bordered input-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-basic-white"
            />
            <p className="text-xs text-basic-light/40 mt-1">
              Where users will land after clicking the ad.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-basic-light/70 mb-1.5">
            Headline (optional)
          </label>
          <input
            type="text"
            placeholder="Your headline text"
            value={overrides.headline ?? ""}
            onChange={e => onChange({ ...overrides, headline: e.target.value })}
            className="input input-bordered input-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-basic-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-basic-light/70 mb-1.5">
            Primary Text (optional)
          </label>
          <textarea
            placeholder="Override the post caption text"
            value={overrides.primaryText ?? ""}
            onChange={e =>
              onChange({ ...overrides, primaryText: e.target.value })
            }
            rows={3}
            className="textarea textarea-bordered w-full bg-system-gray-900/50 border-system-gray-700/50 text-basic-white"
          />
        </div>

        {ctaOptions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-basic-light/70 mb-1.5">
              Call to Action
            </label>
            <select
              value={overrides.callToAction ?? ""}
              onChange={e =>
                onChange({
                  ...overrides,
                  callToAction: e.target.value || undefined,
                })
              }
              className="select select-bordered select-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-basic-white"
            >
              <option value="">Select a CTA...</option>
              {ctaOptions.map(cta => (
                <option key={cta} value={cta}>
                  {formatCTA(cta)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <button className="btn btn-ghost" onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <button
          className="btn btn-primary"
          disabled={!isValid}
          onClick={onNext}
        >
          Continue
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
